import axios, { AxiosError } from 'axios';
import { Podcast, Idea, Insight, Quote, Product } from '../types/index';
import { Episode as EpisodeType } from '../types/index';
import type {
  EpisodeSearchResponse,
  GuestSearchResponse,
  SearchFilters,
} from '../types/index';
import type {
  SendMessageRequest,
  SendMessageResponse,
  SessionsResponse,
  SessionDetailResponse,
  IndexStatus,
  ChatSession,
  PodcastIndexingStatus,
  PodcastChatResponse,
  SourceCitation,
  ReferencedEpisode,
} from '../types/chat';
import { supabase } from '../lib/supabase';
import { savePathAndRedirectToLogin, getAndClearReturnPath } from '../lib/auth';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
// Ensure HTTPS in production
const secureApiUrl = import.meta.env.PROD ? API_URL.replace('http://', 'https://') : API_URL;

export const api = axios.create({
  baseURL: `${secureApiUrl}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  maxRedirects: 5
});

// Add request interceptor for authentication
api.interceptors.request.use(
  async (config) => {
    // Get Supabase session
    const { data: { session } } = await supabase.auth.getSession();

    // Add debug log for every request
    // console.log('API request auth state:', {
    //   hasSession: !!session,
    //   hasAccessToken: !!session?.access_token,
    //   url: config.url,
    //   method: config.method,
    //   timestamp: new Date().toISOString()
    // });

    if (session?.access_token) {
      // console.log('Using Supabase session token for API request');
      config.headers.Authorization = `Bearer ${session.access_token}`;
    } else {
      // Try to get user if session might not be initialized yet
      const { data: { user } } = await supabase.auth.getUser();

      console.log('Auth state detail check:', {
        hasUser: !!user,
        userId: user?.id,
        sessionMissing: !session,
        timestamp: new Date().toISOString()
      });

      if (user) {
        // If we have a user but no session, it could be a race condition
        // Try to get a fresh session
        console.log('Session missing but user exists, refreshing session');
        const { data: refreshData } = await supabase.auth.refreshSession();
        if (refreshData.session?.access_token) {
          config.headers.Authorization = `Bearer ${refreshData.session.access_token}`;
          console.log('Session refreshed successfully, token applied');
        } else {
          console.log('Failed to refresh session', { hasUser: !!user });
        }
      } else {
        // Fallback to legacy token if exists
        const legacyToken = localStorage.getItem('token');
        if (legacyToken) {
          console.log('Using legacy token for API request');
          config.headers.Authorization = `Bearer ${legacyToken}`;
        } else {
          console.log('No authentication token available for request');
        }
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for better error handling
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    console.error('API Error:', {
      status: error.response?.status,
      data: error.response?.data,
      headers: error.response?.headers,
      config: error.config
    });

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      // Clear the legacy token if it exists
      localStorage.removeItem('token');

      // Get current path and search params
      const currentPath = window.location.pathname + window.location.search;

      // Use the utility function to save path and redirect
      savePathAndRedirectToLogin(currentPath);
    }

    return Promise.reject(error);
  }
);

export const login = async (email: string, password: string): Promise<{ access_token: string }> => {
  try {
    const params = new URLSearchParams();
    params.append('username', email);
    params.append('password', password);

    const response = await api.post('/auth/login/', params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    const { access_token } = response.data;
    localStorage.setItem('token', access_token);
    return response.data;
  } catch (error) {
    console.error('Failed to login:', error);
    throw error;
  }
};

export const logout = async (): Promise<void> => {
  try {
    // Clear legacy token if it exists
    localStorage.removeItem('token');

    // Attempt to sign out from Supabase
    // This will handle clearing the session and local storage
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Supabase logout error:', error);
      throw error;
    }
  } catch (error) {
    console.error('Failed to logout:', error);
    throw error;
  }
};

export const getPodcasts = async (): Promise<Podcast[]> => {
  try {
    const response = await api.get('/podcasts/');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch podcasts:', error);
    throw error;
  }
};

export const getPodcast = async (id: string): Promise<Podcast> => {
  try {
    const response = await api.get(`/podcasts/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch podcast ${id}:`, error);
    throw error;
  }
};

/**
 * Fetches episodes for a specific podcast
 * @param id - The podcast ID (not the custom name)
 * @returns A promise that resolves to an array of episodes
 */
export const getEpisodes = async (id: string): Promise<EpisodeType[]> => {
  try {
    const response = await api.get(`/episodes/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch episodes for podcast ${id}:`, error);
    throw error;
  }
};

export const getEpisodeDetails = async (episodeId: string): Promise<EpisodeType> => {
  try {
    const response = await api.get(`/episodes/details/${episodeId}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch episode details for episode ${episodeId}:`, error);
    throw error;
  }
};

export const getIdeas = async (episodeId: string): Promise<Idea[]> => {
  try {
    const response = await api.get(`/ideas/episode/${episodeId}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch ideas for episode ${episodeId}:`, error);
    throw error;
  }
};

interface GetInsightsParams {
  min_ai_quality_score?: number;
  max_ai_quality_score?: number;
  skip?: number;
  limit?: number;
}

export const getInsights = async (episodeId: string, params: GetInsightsParams = {}): Promise<Insight[]> => {
  try {
    const queryParams = new URLSearchParams();

    if (params.min_ai_quality_score !== undefined) {
      queryParams.set('min_ai_quality_score', params.min_ai_quality_score.toString());
    }
    if (params.max_ai_quality_score !== undefined) {
      queryParams.set('max_ai_quality_score', params.max_ai_quality_score.toString());
    }
    if (params.skip !== undefined) {
      queryParams.set('skip', params.skip.toString());
    }
    if (params.limit !== undefined) {
      queryParams.set('limit', params.limit.toString());
    }

    const url = `/insights/${episodeId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get(url);
    return response.data.items || [];
  } catch (error) {
    console.error(`Failed to fetch insights for episode ${episodeId}:`, error);
    return [];
  }
};

interface GetQuotesParams {
  min_ai_quality_score?: number;
  max_ai_quality_score?: number;
  skip?: number;
  limit?: number;
}

export const getQuotes = async (episodeId: string, params: GetQuotesParams = {}): Promise<Quote[]> => {
  try {
    const queryParams = new URLSearchParams();

    if (params.min_ai_quality_score !== undefined) {
      queryParams.set('min_ai_quality_score', params.min_ai_quality_score.toString());
    }
    if (params.max_ai_quality_score !== undefined) {
      queryParams.set('max_ai_quality_score', params.max_ai_quality_score.toString());
    }
    if (params.skip !== undefined) {
      queryParams.set('skip', params.skip.toString());
    }
    if (params.limit !== undefined) {
      queryParams.set('limit', params.limit.toString());
    }

    const url = `/quotes/${episodeId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get(url);
    return response.data.items || [];
  } catch (error) {
    console.error(`Failed to fetch quotes for episode ${episodeId}:`, error);
    return [];
  }
};

export const getProducts = async (episodeId: string): Promise<Product[]> => {
  try {
    const response = await api.get(`/products/${episodeId}`);
    return response.data.items || [];
  } catch (error) {
    console.error(`Failed to fetch products for episode ${episodeId}:`, error);

    // Log more detailed error information
    if (error instanceof Error) {
      console.error('Error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack,
      });

      // If it's an AxiosError, log response data
      if ('response' in error && error.response && typeof error.response === 'object') {
        const axiosError = error as AxiosError;
        console.error('Server response:', {
          status: axiosError.response?.status,
          data: axiosError.response?.data,
        });
      }
    }

    // Return empty array instead of throwing to prevent component crashes
    return [];
  }
};

/**
 * Fetches random insights from the backend API with optional quality filtering
 * @param count - Number of random insights to fetch (default: 4)
 * @param minQualityScore - Minimum AI quality score to filter by (default: 70 for high quality)
 * @returns A promise that resolves to an array of random insights with episode and podcast data
 */
export const getRandomInsights = async (
  count: number = 4,
  minQualityScore: number = 70
): Promise<Array<Insight & { episode: EpisodeType; podcast: Podcast }>> => {
  try {
    // console.log('Fetching random insights from API:', { count, minQualityScore });

    const queryParams = new URLSearchParams();
    queryParams.set('limit', count.toString());
    queryParams.set('min_ai_quality_score', minQualityScore.toString());

    const url = `/insights/random?${queryParams.toString()}`;
    const response = await api.get(url);


    return response.data;
  } catch (error) {
    console.error('Failed to fetch random insights:', error);
    return [];
  }
};

export const updateIdeaVotes = async (ideaId: string, type: 'upvote' | 'downvote'): Promise<Idea> => {
  try {
    const response = await api.post(`/ideas/${ideaId}/vote`, { type });
    return response.data;
  } catch (error) {
    console.error('Failed to update vote:', error);
    throw error;
  }
};

interface GetIdeasParams {
  page?: number;
  sortBy?: string;
  sortOrder?: string;
  businessArea?: string;
  keyword?: string;
}

export const getAllIdeas = async (params: GetIdeasParams = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.set('page', params.page.toString());
    if (params.sortBy) queryParams.set('sort_by', params.sortBy);
    if (params.sortOrder) queryParams.set('sort_order', params.sortOrder);
    if (params.businessArea) queryParams.set('business_area', params.businessArea);
    if (params.keyword) queryParams.set('keyword', params.keyword);

    const response = await api.get(`/ideas/?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch ideas:', error);
    throw error;
  }
};

export const getBusinessAreas = async (): Promise<string[]> => {
  const startTime = performance.now();
  try {
    console.log('[getBusinessAreas] Starting business areas fetch');

    // Check cache first (with error handling for storage access)
    let cachedAreas: string | null = null;
    let cacheTimestamp: string | null = null;

    try {
      cachedAreas = localStorage.getItem('businessAreas');
      cacheTimestamp = localStorage.getItem('businessAreasTimestamp');
    } catch (storageError) {
      console.warn('[getBusinessAreas] localStorage access denied:', storageError);
      // Continue without cache if storage is not accessible
    }

    // Cache is valid for 24 hours
    const CACHE_DURATION = 12 * 60 * 60 * 1000; // 24 hours in milliseconds
    // const CACHE_DURATION = 1000; // 1 milliseconds
    if (cachedAreas && cacheTimestamp) {
      const timeSinceCache = Date.now() - Number(cacheTimestamp);
      const isValid = timeSinceCache < CACHE_DURATION;

      // console.log('[getBusinessAreas] Cache status:', {
      //   isValid,
      //   timeSinceCache: `${Math.round(timeSinceCache / 1000 / 60)} minutes`,
      //   cacheExpiresIn: `${Math.round((CACHE_DURATION - timeSinceCache) / 1000 / 60)} minutes`
      // });

      if (isValid) {
        const parsedAreas = JSON.parse(cachedAreas);
        console.log('[getBusinessAreas] Cache hit, returning', {
          areaCount: parsedAreas.length,
          areas: parsedAreas
        });
        return parsedAreas;
      }
    } else {
      // console.log('[getBusinessAreas] No cache found or cache timestamp missing');
    }

    // If cache is invalid or doesn't exist, fetch from API
    // console.log('[getBusinessAreas] Fetching from API');
    const response = await api.get('/ideas/business-areas');
    const areas = response.data;

    // console.log('[getBusinessAreas] API response received', {
    //   status: response.status,
    //   areaCount: areas.length,
    //   areas
    // });

    // Update cache (with error handling for storage access)
    try {
      localStorage.setItem('businessAreas', JSON.stringify(areas));
      localStorage.setItem('businessAreasTimestamp', Date.now().toString());
      // console.log('[getBusinessAreas] Cache updated');
    } catch (storageError) {
      console.warn('[getBusinessAreas] Failed to update localStorage cache:', storageError);
      // Continue without caching if storage is not accessible
    }

    // const endTime = performance.now();
    // console.log('[getBusinessAreas] Operation completed in', {
    //   duration: `${Math.round(endTime - startTime)}ms`
    // });

    return areas;
  } catch (error) {
    console.error('[getBusinessAreas] Error fetching business areas:', {
      error,
      errorMessage: error instanceof Error ? error.message : 'Unknown error',
      errorStack: error instanceof Error ? error.stack : undefined
    });

    // If API call fails, try to use cached data even if expired (with error handling)
    try {
      const cachedAreas = localStorage.getItem('businessAreas');
      if (cachedAreas) {
        console.log('[getBusinessAreas] Falling back to expired cache');
        const parsedAreas = JSON.parse(cachedAreas);
        console.log('[getBusinessAreas] Returning expired cache data', {
          areaCount: parsedAreas.length,
          areas: parsedAreas
        });
        return parsedAreas;
      }
    } catch (storageError) {
      console.warn('[getBusinessAreas] Failed to access localStorage for fallback:', storageError);
      // If storage is not accessible, we'll throw the original error below
    }

    const endTime = performance.now();
    console.log('[getBusinessAreas] Operation failed in', {
      duration: `${Math.round(endTime - startTime)}ms`
    });

    throw error;
  }
};

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// Track in-flight save requests to prevent duplicates
const inFlightSaveRequests = new Map<string, Promise<Idea>>();

// Original save idea function
const _saveIdea = async (ideaId: string): Promise<Idea> => {
  const response = await api.post(`/ideas/${ideaId}/save`);
  return response.data;
};

// Wrapped save idea function with request deduplication
export const saveIdea = async (ideaId: string): Promise<Idea> => {
  // If there's already an in-flight request for this idea, return that promise
  if (inFlightSaveRequests.has(ideaId)) {
    console.log(`Using existing save request for idea: ${ideaId}`);
    return inFlightSaveRequests.get(ideaId)!;
  }

  // Create a new request promise
  const savePromise = _saveIdea(ideaId)
    .finally(() => {
      // Remove from in-flight requests after a short delay
      setTimeout(() => {
        inFlightSaveRequests.delete(ideaId);
      }, 500);
    });

  // Store the promise in the map
  inFlightSaveRequests.set(ideaId, savePromise);

  return savePromise;
};

// Original unsave idea function
const _unsaveIdea = async (ideaId: string): Promise<Idea> => {
  const response = await api.delete(`/ideas/${ideaId}/save`);
  return response.data;
};

// Wrapped unsave idea function with request deduplication
export const unsaveIdea = async (ideaId: string): Promise<Idea> => {
  // If there's already an in-flight request for this idea, return that promise
  if (inFlightSaveRequests.has(ideaId)) {
    console.log(`Using existing unsave request for idea: ${ideaId}`);
    return inFlightSaveRequests.get(ideaId)!;
  }

  // Create a new request promise
  const unsavePromise = _unsaveIdea(ideaId)
    .finally(() => {
      // Remove from in-flight requests after a short delay
      setTimeout(() => {
        inFlightSaveRequests.delete(ideaId);
      }, 500);
    });

  // Store the promise in the map
  inFlightSaveRequests.set(ideaId, unsavePromise);

  return unsavePromise;
};

export const getSavedIdeas = async (page: number = 1, pageSize: number = 10): Promise<PaginatedResponse<Idea>> => {
  console.log('getSavedIdeas called with page:', page, 'pageSize:', pageSize);
  try {
    const response = await api.get(`/ideas/saved`, {
      params: {
        page,
        page_size: pageSize
      }
    });
    console.log('getSavedIdeas response:', response.data);
    return response.data;
  } catch (error) {
    console.error('getSavedIdeas error:', error);
    throw error;
  }
};

export const subscribeToNewsletter = async (email: string, source: string = 'footer'): Promise<{ status: string; message: string }> => {
  try {
    const response = await api.post('/subscribers/', {
      email,
      source
    });
    return response.data;
  } catch (error) {
    console.error('Failed to subscribe to newsletter:', error);
    if (error instanceof AxiosError && error.response) {
      throw new Error(error.response.data.detail || 'Failed to subscribe. Please try again.');
    }
    throw new Error('Failed to subscribe. Please try again.');
  }
};

/**
 * Sets up a global auth state change listener
 * Redirects after successful sign-in based on the return path
 */
export function setupAuthRedirects(): void {
  // Subscribe to auth state changes
  supabase.auth.onAuthStateChange((event, session) => {
    // console.log('Auth state changed:', { 
    //   event, 
    //   hasSession: !!session,
    //   url: window.location.href
    // });

    // Skip handling redirects for the callback route - this is now managed by AuthCallback component
    if (window.location.pathname.includes('/auth/callback')) {
      console.log('Auth callback detected - skipping api.ts redirect handler (handled by AuthCallback component)');
      return;
    }

    // Handle sign-in and initial session events
    if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
      // Store the session data in localStorage for resilience
      if (session) {
        localStorage.setItem('supabase_auth_event', event);
        localStorage.setItem('supabase_auth_user', session.user?.email || '');
        localStorage.setItem('supabase_auth_timestamp', new Date().toISOString());
      }

      // If we're on the callback page, handle redirection
      if (window.location.pathname.includes('/auth/callback')) {
        try {
          // Get the return path from storage with race condition protection
          const returnPath = getAndClearReturnPath();

          console.log('Auth callback detected, redirecting to:', {
            returnPath,
            currentPath: window.location.pathname,
            event,
            hasUser: !!session?.user,
            timestamp: new Date().toISOString()
          });

          // Only navigate if we're not already on the target path (prevents redirect loops)
          if (returnPath && returnPath !== window.location.pathname) {
            // Add a small delay to ensure any pending auth operations complete
            setTimeout(() => {
              window.location.href = returnPath;
            }, 300); // Increased delay for better reliability
          } else {
            // Fallback to home if no return path
            setTimeout(() => {
              window.location.href = '/';
            }, 300);
          }
        } catch (error) {
          console.error('Error during auth redirect:', error);
          // Fallback to home page if there's an error
          setTimeout(() => {
            window.location.href = '/';
          }, 300);
        }
      }
    }
  });
}

/**
 * Search episodes with hybrid or metadata-only mode
 */
export const searchEpisodes = async (
  query: string,
  filters?: SearchFilters
): Promise<EpisodeSearchResponse> => {
  const params = new URLSearchParams({
    q: query,
    ...(filters?.podcastId && { podcast_id: filters.podcastId }),
    ...(filters?.useHybrid !== undefined && { use_hybrid: String(filters.useHybrid) }),
    ...(filters?.limit && { limit: String(filters.limit) }),
  });

  const { data } = await api.get<EpisodeSearchResponse>(
    `/search/episodes?${params.toString()}`
  );
  return data;
};

/**
 * Search episodes by guest name (fuzzy matching)
 */
export const searchEpisodesByGuest = async (
  guestName: string,
  filters?: Omit<SearchFilters, 'useHybrid'>
): Promise<GuestSearchResponse> => {
  const params = new URLSearchParams({
    guest_name: guestName,
    ...(filters?.podcastId && { podcast_id: filters.podcastId }),
    ...(filters?.limit && { limit: String(filters.limit) }),
  });

  const { data } = await api.get<GuestSearchResponse>(
    `/search/episodes/by-guest?${params.toString()}`
  );
  return data;
};

// Chat API endpoints
export const chatAPI = {
  // Send message (create session if needed)
  sendMessage: async (episodeId: string, data: SendMessageRequest): Promise<SendMessageResponse> => {
    try {
      const response = await api.post<SendMessageResponse>(
        `/episodes/${episodeId}/chat`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Failed to send message:', error);
      if (error instanceof AxiosError) {
        if (error.response?.status === 403) {
          throw new Error('Episode not indexed yet. Please index the episode first.');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests. Please wait a moment before sending another message.');
        }
        if (error.response?.status === 404) {
          throw new Error('Session not found. Please start a new conversation.');
        }
      }
      throw error;
    }
  },

  // Send message with streaming (SSE)
  sendMessageStream: async (
    episodeId: string,
    data: SendMessageRequest,
    callbacks: {
      onToken: (token: string) => void;
      onSources: (sources: SourceCitation[]) => void;
      onSessionId: (sessionId: string) => void;
      /** Called when stream ends. Optional payload may contain final content/sources from backend. */
      onDone: (payload?: { content?: string; sources?: SourceCitation[] }) => void;
      onError: (error: Error) => void;
    }
  ): Promise<void> => {
    const url = `${secureApiUrl}/api/v1/episodes/${episodeId}/chat/stream`;

    try {
      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error('Authentication required');
      }

      // Make POST request with streaming
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorText = await response.text();
        if (response.status === 403) {
          throw new Error('Episode not indexed yet. Please index the episode first.');
        }
        if (response.status === 429) {
          throw new Error('Too many requests. Please wait a moment before sending another message.');
        }
        if (response.status === 404) {
          throw new Error('Session not found. Please start a new conversation.');
        }
        throw new Error(`Stream request failed: ${response.status} ${errorText}`);
      }

      // Read the stream
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body is not readable');
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('event:')) {
            currentEvent = line.substring(6).trim();
          } else if (line.startsWith('data:')) {
            const dataStr = line.substring(5).trim();
            if (!dataStr) continue;

            try {
              const data = JSON.parse(dataStr);

              switch (currentEvent) {
                case 'session_id': {
                  // Backend may send raw JSON string ("uuid") or object { content: "uuid" }
                  const sessionId =
                    typeof data === 'string'
                      ? data
                      : (typeof data?.content === 'string' ? data.content : undefined);
                  if (sessionId) callbacks.onSessionId(sessionId);
                  break;
                }
                case 'sources': {
                  // Backend may send raw JSON array or object { content: [...] }
                  const sourcesData = Array.isArray(data) ? data : (data?.content ?? []);
                  callbacks.onSources(sourcesData);
                  break;
                }
                case 'token': {
                  // Backend may send raw JSON string ("token") or object { content: "token" }
                  const tokenContent = typeof data === 'string' ? data : data?.content;
                  // Only invoke when content is defined to avoid "undefined" in UI
                  if (typeof tokenContent === 'string') {
                    callbacks.onToken(tokenContent);
                  }
                  break;
                }
                case 'done': {
                  const payload =
                    data && typeof data === 'object'
                      ? {
                          content: typeof data.content === 'string' ? data.content : undefined,
                          sources: Array.isArray(data.sources) ? data.sources : undefined,
                        }
                      : undefined;
                  callbacks.onDone(payload);
                  return; // Exit the function
                }
              }
            } catch (parseError) {
              console.error('Failed to parse SSE data:', dataStr, parseError);
            }
          }
        }
      }

      // Stream ended without 'done' event
      callbacks.onDone(undefined);
    } catch (error) {
      console.error('Stream error:', error);
      callbacks.onError(error instanceof Error ? error : new Error('Stream connection failed'));
    }
  },

  // List all sessions for episode or podcast.
  // Backend returns a raw array (List[ChatSessionSchema]); normalize to { sessions, total }.
  listSessions: async (episodeId?: string, podcastId?: string): Promise<SessionsResponse> => {
    try {
      const params: { episode_id?: string; podcast_id?: string } = {};
      if (episodeId) params.episode_id = episodeId;
      if (podcastId) params.podcast_id = podcastId;
      const response = await api.get<ChatSession[] | SessionsResponse>('/chat/sessions', {
        params: Object.keys(params).length ? params : undefined
      });
      const data = response.data;
      if (Array.isArray(data)) {
        return { sessions: data, total: data.length };
      }
      return {
        sessions: Array.isArray(data?.sessions) ? data.sessions : [],
        total: data?.total ?? 0,
      };
    } catch (error) {
      console.error('Failed to list sessions:', error);
      throw error;
    }
  },

  // Get session with messages
  getSession: async (sessionId: string): Promise<SessionDetailResponse> => {
    if (!sessionId || sessionId === 'undefined') {
      throw new Error('Invalid session id');
    }
    try {
      const response = await api.get<SessionDetailResponse>(
        `/chat/sessions/${sessionId}`
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to get session ${sessionId}:`, error);
      if (error instanceof AxiosError && error.response?.status === 404) {
        throw new Error('Session not found.');
      }
      throw error;
    }
  },

  // Delete session
  deleteSession: async (sessionId: string): Promise<void> => {
    try {
      await api.delete(`/chat/sessions/${sessionId}`);
    } catch (error) {
      console.error(`Failed to delete session ${sessionId}:`, error);
      throw error;
    }
  },

  // Check indexing status
  getIndexStatus: async (episodeId: string): Promise<IndexStatus> => {
    try {
      const response = await api.get<IndexStatus>(
        `/episodes/${episodeId}/index-status`
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to get index status for episode ${episodeId}:`, error);
      throw error;
    }
  },

  // Trigger indexing
  triggerIndexing: async (episodeId: string): Promise<void> => {
    try {
      await api.post(`/episodes/${episodeId}/index-transcript`);
    } catch (error) {
      console.error(`Failed to trigger indexing for episode ${episodeId}:`, error);
      throw error;
    }
  },

  // Create new session explicitly
  createSession: async (episodeId: string, title?: string): Promise<ChatSession> => {
    try {
      // Send an initial message to create the session
      const response = await chatAPI.sendMessage(episodeId, {
        message: title || 'New conversation',
        session_id: undefined,
      });

      // Return the session from the response
      // Note: The backend creates a session automatically when sending the first message
      return {
        id: response.session_id,
        user_id: '', // Will be filled by backend
        episode_id: episodeId,
        chat_scope: 'episode',
        title: title || 'New conversation',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        last_active: new Date().toISOString(),
        message_count: 2,
      };
    } catch (error) {
      console.error('Failed to create session:', error);
      throw error;
    }
  },

  // --- Podcast-level chat ---

  /** Get indexing status for all episodes in a podcast */
  getPodcastIndexStatus: async (podcastId: string): Promise<PodcastIndexingStatus> => {
    try {
      const response = await api.get<PodcastIndexingStatus>(
        `/podcasts/${podcastId}/index-status`
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to get podcast index status for ${podcastId}:`, error);
      if (error instanceof AxiosError && error.response?.status === 404) {
        throw new Error('Podcast not found. This podcast may not be available for chat yet.');
      }
      throw error;
    }
  },

  /**
   * Send a message in podcast-level chat.
   * Backend returns ChatResponse (session_id, response, sources, is_new_session).
   */
  sendPodcastMessage: async (
    podcastId: string,
    data: SendMessageRequest
  ): Promise<PodcastChatResponse> => {
    try {
      const response = await api.post<PodcastChatResponse>(
        `/podcasts/${podcastId}/chat`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Failed to send podcast chat message:', error);
      if (error instanceof AxiosError && error.response) {
        const detail = typeof error.response.data === 'object' && error.response.data !== null && 'detail' in error.response.data
          ? String((error.response.data as { detail?: string }).detail)
          : null;
        if (error.response.status === 400) {
          throw new Error(detail || 'No episodes indexed yet. Index at least one episode to chat with this podcast.');
        }
        if (error.response.status === 429) {
          throw new Error('Too many requests. Please wait a moment before sending another message.');
        }
        if (error.response.status === 404) {
          const friendly =
            detail && detail !== 'Not Found'
              ? detail
              : 'Podcast chat is not available. The server may not have this endpoint yet, or the podcast may not be set up for chat—try again later or contact support.';
          throw new Error(friendly);
        }
      }
      throw error;
    }
  },

  // Send podcast message with streaming (SSE)
  sendPodcastMessageStream: async (
    podcastId: string,
    data: SendMessageRequest,
    callbacks: {
      onToken: (token: string) => void;
      onSources: (sources: SourceCitation[]) => void;
      onSessionId: (sessionId: string) => void;
      onReferencedEpisodes?: (episodes: ReferencedEpisode[]) => void;
      /** Called when stream ends. Optional payload may contain final content/sources/referenced_episodes from backend. */
      onDone: (payload?: { content?: string; sources?: SourceCitation[]; referenced_episodes?: ReferencedEpisode[] }) => void;
      onError: (error: Error) => void;
    }
  ): Promise<void> => {
    const url = `${secureApiUrl}/api/v1/podcasts/${podcastId}/chat/stream`;

    try {
      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error('Authentication required');
      }

      // Make POST request with streaming
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorText = await response.text();
        if (response.status === 400) {
          throw new Error('No episodes indexed yet. Index at least one episode to chat with this podcast.');
        }
        if (response.status === 429) {
          throw new Error('Too many requests. Please wait a moment before sending another message.');
        }
        if (response.status === 404) {
          throw new Error('Podcast chat is not available. The server may not have this endpoint yet.');
        }
        throw new Error(`Stream request failed: ${response.status} ${errorText}`);
      }

      // Read the stream
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body is not readable');
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('event:')) {
            currentEvent = line.substring(6).trim();
          } else if (line.startsWith('data:')) {
            const dataStr = line.substring(5).trim();
            if (!dataStr) continue;

            try {
              const data = JSON.parse(dataStr);

              switch (currentEvent) {
                case 'session_id': {
                  // Backend may send raw JSON string ("uuid") or object { content: "uuid" }
                  const sessionId =
                    typeof data === 'string'
                      ? data
                      : (typeof data?.content === 'string' ? data.content : undefined);
                  if (sessionId) callbacks.onSessionId(sessionId);
                  break;
                }
                case 'sources': {
                  // Backend may send raw JSON array or object { content: [...] }
                  const sourcesDataPodcast = Array.isArray(data) ? data : (data?.content ?? []);
                  callbacks.onSources(sourcesDataPodcast);
                  break;
                }
                case 'referenced_episodes': {
                  // Backend sends array of referenced episodes
                  const episodesData = Array.isArray(data) ? data : (data?.content ?? []);
                  if (callbacks.onReferencedEpisodes) {
                    callbacks.onReferencedEpisodes(episodesData);
                  }
                  break;
                }
                case 'token': {
                  // Backend may send raw JSON string ("token") or object { content: "token" }
                  const tokenContentPodcast = typeof data === 'string' ? data : data?.content;
                  // Only invoke when content is defined to avoid "undefined" in UI
                  if (typeof tokenContentPodcast === 'string') {
                    callbacks.onToken(tokenContentPodcast);
                  }
                  break;
                }
                case 'done': {
                  const payloadPodcast =
                    data && typeof data === 'object'
                      ? {
                          content: typeof data.content === 'string' ? data.content : undefined,
                          sources: Array.isArray(data.sources) ? data.sources : undefined,
                          referenced_episodes: Array.isArray(data.referenced_episodes) ? data.referenced_episodes : undefined,
                        }
                      : undefined;
                  callbacks.onDone(payloadPodcast);
                  return; // Exit the function
                }
              }
            } catch (parseError) {
              console.error('Failed to parse SSE data:', dataStr, parseError);
            }
          }
        }
      }

      // Stream ended without 'done' event
      callbacks.onDone(undefined);
    } catch (error) {
      console.error('Stream error:', error);
      callbacks.onError(error instanceof Error ? error : new Error('Stream connection failed'));
    }
  },

  /** Create a new podcast-level chat session (sends first message to create session) */
  createPodcastSession: async (podcastId: string, title?: string): Promise<ChatSession> => {
    try {
      const response = await chatAPI.sendPodcastMessage(podcastId, {
        message: title || 'New conversation',
        session_id: undefined,
      });
      return {
        id: response.session_id,
        user_id: '',
        podcast_id: podcastId,
        chat_scope: 'podcast',
        title: title || 'New conversation',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        last_active: new Date().toISOString(),
        message_count: 2,
      };
    } catch (error) {
      console.error('Failed to create podcast session:', error);
      throw error;
    }
  },
}; 
